/**
 * ZAGX v2 — Data Integrity Reconciler
 *
 * Cross-checks trades ↔ cashTxs ↔ realized P&L to detect and fix mismatches.
 * Runs on app load and can be triggered manually from Settings.
 *
 * Design: read-only audit by default. Repairs only when explicitly called.
 */

import { S, save, stampItem } from './state.js';
import { uid, now, f, fByCur, cashSumByCurrency } from './utils.js';

/**
 * Audit result structure:
 * { ok: boolean, issues: Array<{ type, severity, msg, fix? }> }
 *
 * severity: 'critical' | 'warning' | 'info'
 * fix: optional function to auto-repair
 */

export function auditDataIntegrity() {
  const issues = [];

  // ── Diagnostic dump ──
  console.log('[Reconcile] Audit starting — trades:', S.trades?.length, 'realized:', S.realized?.length, 'cashTxs:', S.cashTxs?.length);
  console.log('[Reconcile] Trade statuses:', S.trades?.map(t => `${t.ticker}:${t.status}`).join(', '));
  if (S.realized?.length) {
    console.log('[Reconcile] Realized entries:', S.realized.map(r => `${r.ticker} qty=${r.qty} pnl=${r.pnl}`).join(', '));
  }

  // ── 1. Every open trade must have a matching trade_open cashTx ──
  const openTrades = S.trades.filter(t => t.status !== 'Closed');
  openTrades.forEach(t => {
    if (t.type === 'TimeDeposit') {
      // TD trades use td_open type
      const hasTx = S.cashTxs.some(tx =>
        (tx.type === 'td_open' || tx.type === 'trade_open') &&
        (tx.tradeId === t.id || (tx.note && tx.note.includes(t.ticker)))
      );
      if (!hasTx) {
        const cur = t.currency || 'TRY';
        const principal = t.td?.principal || t.entry;
        issues.push({
          type: 'missing_open_tx',
          severity: 'warning',
          movesMoney: true,
          msg: `TD ${t.ticker}: no matching cash transaction for open (${fByCur(cur, principal)} ${cur})`,
          fix: () => {
            S.cashTxs.push(stampItem({
              id: uid(), date: t.openDate || now(), type: 'td_open',
              tradeId: t.id, amount: -principal, currency: cur,
              note: `[Reconciled] Open TimeDeposit ${t.ticker}`
            }));
          }
        });
      }
    } else {
      const cost = t.entry * (t._originalQty || t.qty);
      const hasTx = S.cashTxs.some(tx =>
        tx.type === 'trade_open' &&
        (tx.tradeId === t.id || (tx.note && tx.note.includes(t.ticker)))
      );
      if (!hasTx) {
        const cur = t.currency || 'USD';
        issues.push({
          type: 'missing_open_tx',
          severity: 'warning',
          movesMoney: true,
          msg: `Trade ${t.ticker}: no matching trade_open cashTx (${fByCur(cur, cost)} ${cur})`,
          fix: () => {
            S.cashTxs.push(stampItem({
              id: uid(), date: t.openDate || now(), type: 'trade_open',
              tradeId: t.id, amount: -cost, currency: cur,
              note: `[Reconciled] Open ${t.ticker} ${t.side}`
            }));
          }
        });
      }
    }
  });

  // ── 2. Every realized entry must have a matching trade_exit cashTx ──
  //
  // ⚠⚠⚠ THIS WAS A MONEY PUMP AND IT FIRED ON HIS LIVE BOOK — 2026-08-17, eight loads,
  // TRY cash 396,469.31 -> 6,938,628.01.
  //
  // The condition was a TERNARY: `r.tradeId ? tx.tradeId === r.tradeId : (note+date)`. So a
  // realized row that HAS a `tradeId` was matched by id ALONE — and **no writer has ever stamped
  // `tradeId` on a `trade_exit` cashTx**, including the repair below. The moment migration v53
  // back-filled `tradeId` onto five realized rows, four of them stopped matching their REAL exit
  // rows (the fifth, a deposit, was caught by the `td_close` fallback further down),
  // `missing_exit_tx` fired at severity `critical`, and `main.js` applied the repair on load:
  // `S.cash[cur] += proceeds`, a row pushed, `save()`, `cloudSave()`. The pushed row carried no
  // `tradeId` either, so the next load found the same four unmatched and did it again.
  //
  //   per load  EREGL 62,040.00 · AKBNK 38,925.00 · GWIND 33,400.00 · XAU 683,404.84
  //             = ₺817,769.84 and four rows, x8 loads = ₺6,542,158.72 and 32 rows
  //
  // THREE THINGS WERE WRONG. ALL THREE ARE FIXED, BECAUSE ANY ONE OF THEM ALONE STILL LEAVES A
  // PUMP FOR THE NEXT ROW THAT GAINS A LINK:
  //
  //  1. **AN EXTRA IDENTIFIER MUST NARROW A MATCH, NEVER REPLACE ONE.** `||`, not a ternary.
  //     Making a row MORE identifiable made the reconciler blind to it.
  //  2. **A REPAIR MUST WRITE WHAT ITS OWN DETECTOR READS** — rule #25, the one-legged inverse.
  //     The row created below now carries `tradeId`.
  //  3. **A MONEY-MOVING REPAIR DOES NOT RUN BY ITSELF.** `movesMoney: true` below, and
  //     `main.js` auto-applies only the repairs that move none. A detector bug must cost a
  //     console warning, not a balance.
  S.realized.forEach(r => {
    const hasTx = S.cashTxs.some(tx =>
      tx.type === 'trade_exit' && (
        (r.tradeId && tx.tradeId === r.tradeId) ||
        (tx.note && tx.note.includes(r.ticker) &&
          Math.abs(new Date(tx.date).getTime() - new Date(r.date).getTime()) < 60000)
      )
    );
    if (!hasTx) {
      // Also check for td_close / td_mature
      const hasTdTx = S.cashTxs.some(tx =>
        (tx.type === 'td_close' || tx.type === 'td_mature') &&
        tx.note && tx.note.includes(r.ticker) &&
        Math.abs(new Date(tx.date).getTime() - new Date(r.date).getTime()) < 60000
      );
      if (!hasTdTx) {
        const cur = r.currency || 'USD';
        const _trade = S.trades.find(x => x.ticker === r.ticker);
        const margin = r.entry * r.qty;
        const proceeds = margin + r.pnl;
        issues.push({
          type: 'missing_exit_tx',
          severity: 'critical',
          movesMoney: true,
          msg: `Realized ${r.ticker} (${fByCur(cur, r.pnl)} P&L): no matching trade_exit cashTx — proceeds ${fByCur(cur, proceeds)} not in cash`,
          fix: () => {
            S.cash[cur] = (S.cash[cur] || 0) + proceeds;
            S.cashTxs.push(stampItem({
              id: uid(), date: r.date || now(), type: 'trade_exit',
              // ⚠ THE LINK THE DETECTOR ABOVE ASKS FOR. Without it this repair cannot satisfy its
              // own check, and a second load repairs the same row again — which is the pump.
              tradeId: r.tradeId || undefined,
              amount: proceeds, currency: cur,
              note: `[Reconciled] ${r.ticker} exit (proceeds ${fByCur(cur, proceeds)}, P&L:${fByCur(cur, r.pnl)})`
            }));
          }
        });
      }
    }
  });

  // ── 3. Cash balance should match sum of cashTxs ──
  // A-1: canonical helper excludes `_audit` income-tracking entries (single source of truth, prevents F-1 recurrence)
  const computed = cashSumByCurrency(S.cashTxs);

  ['USD', 'TRY', 'EUR'].forEach(cur => {
    const actual = S.cash?.[cur] || 0;
    const expected = computed[cur] || 0;
    const diff = actual - expected;
    const absDiff = Math.abs(diff);
    if (absDiff > 0.01) { // tolerance for floating point
      const wasTruncated = S._truncated && S._truncated.cashTxs;
      issues.push({
        type: 'cash_mismatch',
        severity: absDiff > 100 ? 'critical' : 'warning',
        movesMoney: true,
        msg: `Cash ${cur} mismatch: balance=${fByCur(cur, actual)}, sum(txs)=${fByCur(cur, expected)}, diff=${fByCur(cur, diff)}${wasTruncated ? ' [truncated — auto-fix disabled]' : ''}`,
        fix: wasTruncated ? null : () => {
          // Sync cash balance to match sum of all cashTxs (the transactions are the source of truth)
          // ONLY when cashTxs hasn't been truncated — truncation removes old txs, making sum unreliable
          //
          // ⚠ RECOMPUTED HERE, NOT CLOSED OVER. `expected` was captured when the AUDIT ran; an
          // earlier fix in the same pass — `missing_exit_tx` pushes a row, `missing_open_tx`
          // pushes a row — invalidates it before this one executes, so the balance was set to a
          // sum that was already out of date and only settled on the NEXT pass. Two passes to
          // land a number is one pass too many for a balance, and it is exactly the shape of
          // MOVE-005: a repair whose input is stale by the time it writes. Found while writing
          // the pump's own test, pinned as a known defect there, and closed here.
          if (!S.cash) S.cash = {};
          const fresh = cashSumByCurrency(S.cashTxs)[cur] || 0;
          console.log(`[Reconcile] Cash ${cur}: ${fByCur(cur, S.cash[cur] || 0)} → ${fByCur(cur, fresh)} (synced to transaction sum)`);
          S.cash[cur] = fresh;
        }
      });
    }
  });

  // ── 4. Closed trades should have exit records ──
  const closedTrades = S.trades.filter(t => t.status === 'Closed');
  closedTrades.forEach(t => {
    if (t.type === 'TimeDeposit') return; // TD closures handled differently
    const hasRealized = S.realized.some(r => r.ticker === t.ticker);
    const hasExits = t.exits && t.exits.length > 0;
    if (!hasRealized && !hasExits) {
      issues.push({
        type: 'orphan_closed',
        severity: 'warning',
        msg: `Trade ${t.ticker} is Closed but has no exit records and no realized P&L entry`
      });
    }
  });

  // ── 5. Open/Partial trade has realized exits but qty/status not updated ──
  // If a trade is "Open" but has realized entries, something is wrong.
  // The old check used _originalQty reconstruction which failed when _originalQty was unset
  // (it computed t.qty + exitedQty as "original", making the current qty always look correct).
  // New logic: any Open trade with realized entries is a problem. Period.
  S.trades.filter(t => t.status === 'Open' || t.status === 'Partial').forEach(t => {
    if (t.type === 'TimeDeposit') return;
    const realizedForTicker = S.realized.filter(r => r.ticker === t.ticker);
    if (!realizedForTicker.length) return;

    const totalExitedQty = realizedForTicker.reduce((sum, r) => sum + (r.qty || 0), 0);
    if (totalExitedQty <= 0) return;

    // If trade is Open (not Partial) and has ANY realized exits → status was never updated
    // If trade is Partial, check if exits account is consistent
    const hasExitsArray = t.exits && t.exits.length > 0;
    const exitsQty = hasExitsArray ? t.exits.reduce((s, e) => s + (e.qty || 0), 0) : 0;

    // Determine true original qty
    const originalQty = t._originalQty || (hasExitsArray ? t.qty + exitsQty : t.qty);
    // If exits array is missing/empty but realized exists → trade was never decremented
    const needsFix = (t.status === 'Open' && totalExitedQty > 0) ||
                     (!hasExitsArray && totalExitedQty > 0) ||
                     (totalExitedQty >= originalQty && t.status !== 'Closed');

    if (needsFix) {
      const expectedRemaining = Math.max(0, originalQty - totalExitedQty);
      issues.push({
        type: 'trade_qty_stale',
        severity: 'critical',
        // ⚠ NOT money — it writes no balance and adds no ledger row — but it rewrites a POSITION:
        // `t.qty`, and it reconstructs `t.exits` from scratch when the array is missing. That is
        // an unattended write to the book on every boot, which is the class MOVE-005 came from,
        // so it waits for a click too. Declared separately from `movesMoney` rather than folded
        // into it, because calling a quantity rewrite "money" would be the kind of loose label
        // this file has already been bitten by.
        rewritesPositions: true,
        msg: `Trade ${t.ticker}: status=${t.status}, qty=${f(t.qty, 2)}, but realized has ${f(totalExitedQty, 2)} exited. Original=${f(originalQty, 2)}, expected remaining=${f(expectedRemaining, 2)}`,
        fix: () => {
          // Reconstruct exits array from realized if missing
          if (!hasExitsArray) {
            t.exits = realizedForTicker.map(r => ({
              date: r.date, price: r.exit, qty: r.qty,
              reason: r.reason || 'Reconciled', pnl: r.pnl,
              pnlUSD: r.pnlUSD, currency: r.currency, leverage: r.leverage || 1
            }));
          }
          t._originalQty = originalQty;
          t.qty = expectedRemaining;
          if (t.qty <= 0) {
            t.status = 'Closed';
            t.closeDate = realizedForTicker[realizedForTicker.length - 1]?.date || now();
          } else {
            t.status = 'Partial';
          }
          stampItem(t);
          console.log(`[Reconcile] Fixed ${t.ticker}: qty=${f(t.qty, 2)}, status=${t.status}, originalQty=${f(originalQty, 2)}`);
        }
      });
    }
  });

  // ── 6. Qty reconciliation: exits sum should match original qty ──
  S.trades.filter(t => t.type !== 'TimeDeposit' && t.exits && t.exits.length > 0).forEach(t => {
    const exitsQty = t.exits.reduce((s, e) => s + (e.qty || 0), 0);
    const originalQty = t._originalQty || (t.qty + exitsQty);
    const drift = Math.abs(originalQty - (t.qty + exitsQty));
    if (drift > 0.001) {
      issues.push({
        type: 'qty_drift',
        severity: 'warning',
        msg: `Trade ${t.ticker}: qty drift detected — original=${f(originalQty, 4)}, remaining=${f(t.qty, 4)}, exited=${f(exitsQty, 4)}, gap=${f(drift, 4)}`
      });
    }
  });

  // ── 7. Duplicate cashTxs check ──
  const txSigs = new Map();
  S.cashTxs.forEach((tx, idx) => {
    const sig = `${tx.type}_${tx.amount}_${tx.currency}_${(tx.date || '').slice(0, 16)}`;
    if (txSigs.has(sig)) {
      const prevIdx = txSigs.get(sig);
      // Only flag if same note too (exact duplicate)
      if (S.cashTxs[prevIdx]?.note === tx.note) {
        issues.push({
          type: 'duplicate_tx',
          severity: 'info',
          msg: `Possible duplicate cashTx: ${tx.type} ${fByCur(tx.currency || 'USD', tx.amount)} "${tx.note?.slice(0, 40)}"`
        });
      }
    }
    txSigs.set(sig, idx);
  });

  // ── Fix orphaned portfolio_buy with amount=0 (F-10 regression) ──
  // These should be deposit+portfolio_buy pairs but F-10 merged them into a single net-zero entry.
  // Detect: portfolio_buy with amount=0 and note containing "deposit+buy" but no matching deposit tx.
  (S.cashTxs || []).forEach((tx, idx) => {
    if (tx.type !== 'portfolio_buy' || tx.amount !== 0) return;
    const note = tx.note || '';
    const m = note.match(/deposit\+buy\s+\$?([\d,.]+)\s*—\s*(.+)/);
    if (!m) return;
    const amt = parseFloat(m[1].replace(/,/g, ''));
    const moLabel = m[2].trim();
    if (!amt || amt <= 0) return;
    // Check if a matching deposit already exists for this month
    const hasDeposit = S.cashTxs.some(t =>
      t.type === 'deposit' && (t.note || '').includes('IBKR') && (t.note || '').includes(moLabel)
    );
    if (hasDeposit) return;
    issues.push({
      type: 'missing_monthly_deposit', severity: 'critical', movesMoney: true,
      msg: `Missing IBKR deposit for ${moLabel} (found portfolio_buy amount=0)`,
      fix: () => {
        // Replace the broken portfolio_buy with proper pair
        tx.type = 'portfolio_buy';
        tx.amount = -amt;
        tx.note = 'Portfolio allocation — ' + moLabel;
        // Insert deposit before allocation
        S.cashTxs.splice(idx + 1, 0, {
          id: uid(), date: tx.date, type: 'deposit',
          amount: amt, currency: 'USD',
          note: 'Monthly IBKR deposit — ' + moLabel,
          _reconciledAt: Date.now()
        });
        console.log(`[Reconcile] Created missing IBKR deposit $${amt} for ${moLabel}`);
      }
    });
  });

  // ── A CLOSED MONTH THAT COMMITTED NEW MONEY MUST CARRY ITS EXTERNAL-CAPITAL ROW ──
  //
  // `reconcileMo` writes the pair that records a month's new money: a `deposit` whose note
  // `capital.js` matches as EXTERNAL_IN, and a `portfolio_buy` twin that nets the cash back to
  // zero. Salary rows are `_audit` MIRRORs by design, so that deposit is the ONLY door through
  // which the monthly contribution becomes external capital.
  //
  // A REBALANCE MONTH NEVER REACHES THAT CODE. August 2026 was funded by selling thirteen
  // positions; its plan holds thirteen `zero-target` entries for tickers that are no longer
  // targets and none at all for the twenty-six that are, so `reconcileMo` refuses at 13/26 and
  // always will. The month closed correctly on the cash-flow side and silently kept its
  // contribution out of `netExtCapital` — which overstates `twPnL` by exactly that amount,
  // permanently, with nothing saying so. The F-10 check above cannot see it: that one looks for
  // a BROKEN row, and here there is no row at all.
  //
  // Detect by the transaction's own month, never by the label: the label changed shape mid-year
  // ("2026-03" became "Apr-2026"), so matching it would have missed the first quarter.
  const _extCapMonths = new Set(
    (S.cashTxs || [])
      .filter(t => t && t.type === 'deposit' && /^monthly ibkr deposit/i.test(t.note || ''))
      .map(t => String(t.date || '').slice(0, 7))
  );
  Object.keys(S.cashflow || {}).sort().forEach(k => {
    const mo = S.cashflow[k];
    if (!mo || !mo.closed) return;            // an open month owes nothing yet
    const comm = Number(mo.portfolioComm) || 0;
    if (!(comm > 0)) return;                  // a month that committed nothing needs no row
    if (_extCapMonths.has(k)) return;
    const mul = (S.monthlyRates && S.monthlyRates[k] && S.monthlyRates[k].mul) || 1;
    const amt = +(((Number(S.base) || 0) * mul).toFixed(2));
    // No base configured is UNKNOWN, not zero. Say nothing rather than invent a figure.
    if (!(amt > 0)) return;
    issues.push({
      type: 'missing_external_capital',
      severity: 'critical',
      // `movesMoney` gates the BOOT path (`main.js` calls `reconcileAndRepair({autoOnly:true})`).
      // `S.cash` does not move here -- the pair nets to zero -- but `netExtCapital` does, and
      // twPnL is measured off it. A repair that silently changes a money FIGURE on page load is
      // the shape that invented TRY 6,542,158.72 across eight loads on 2026-08-17 under a green
      // toast. It waits for a deliberate click in Settings. Cash-neutral is not decision-neutral.
      movesMoney: true,
      msg: `${k}: closed with ${fByCur('TRY', comm)} committed but no "Monthly IBKR deposit" row`
        + ` — ${fByCur('USD', amt)} of external capital is unrecorded, so twPnL reads that much high`,
      fix: () => {
        // Idempotent against partial success: re-derive the set at fix time, because an earlier
        // issue in the same run may already have written this month's pair. (Question 7.)
        const already = (S.cashTxs || []).some(t =>
          t && t.type === 'deposit' && /^monthly ibkr deposit/i.test(t.note || '')
          && String(t.date || '').slice(0, 7) === k);
        if (already) return;
        // The same pair `reconcileMo` writes, in the same order, so the ledger reads identically
        // to every reconciled month. Net cash effect is zero BY CONSTRUCTION — `reconcileMo`
        // computes `S.cash.USD += depositAmt - buyAmt`, which is 0 when the two are equal — and
        // a rebalance month deployed through `+ Lot`, whose own `portfolio_buy` rows already
        // debited the cash. Debiting again here would double-count the deployment.
        const d = k + '-28T12:00:00';
        S.cashTxs.unshift(stampItem({
          id: uid(), date: d, type: 'portfolio_buy', amount: -amt, currency: 'USD',
          note: 'Portfolio allocation — ' + k, _reconciledAt: Date.now()
        }));
        S.cashTxs.unshift(stampItem({
          id: uid(), date: d, type: 'deposit', amount: amt, currency: 'USD',
          note: 'Monthly IBKR deposit — ' + k, _reconciledAt: Date.now()
        }));
        console.log('[Reconcile] Recorded the missing external-capital pair for ' + k + ': $' + amt);
      }
    });
  });

  return {
    ok: !issues.some(i => i.severity === 'critical'),
    issues,
    summary: {
      total: issues.length,
      critical: issues.filter(i => i.severity === 'critical').length,
      warning: issues.filter(i => i.severity === 'warning').length,
      info: issues.filter(i => i.severity === 'info').length
    }
  };
}

/**
 * Run audit and auto-repair fixable issues.
 * Returns audit result (post-repair).
 */
/**
 * ⚠ THE BOOT PATH APPLIES ONLY THE REPAIRS THAT MOVE NO MONEY — 2026-08-17.
 *
 * `main.js` called `reconcileAndRepair()` on every load and it applied EVERYTHING with a `fix`,
 * including `S.cash[cur] += proceeds`, then `save()` and `cloudSave()`. One wrong line in one
 * detector therefore became ₺6,542,158.72 of invented cash across eight page loads, silently,
 * announced by a green toast reading "Data reconciled: 4 issue(s) fixed".
 *
 * A reconciler is a WRITER, and the merge does not know it is special (theme #25). The rule now:
 * **a repair that touches `S.cash` or adds a `cashTxs` row is reported, never auto-applied.** He
 * clicks it in Settings, having read what it intends to do.
 *
 * ⚠ AND THE SWEEP THAT FOLLOWED WIDENED IT. The first version of this note said a stale trade
 * status and a reconstructed `exits` array "still repair themselves on load, because none of it
 * can invent a balance". That is true and it is not the point: `trade_qty_stale` rewrites `t.qty`
 * and can rebuild `t.exits` from nothing, unattended, every boot. Money is not the only thing a
 * wrong detector can destroy. `rewritesPositions` holds it back too.
 */
export function reconcileAndRepair(opts = {}) {
  const autoOnly = opts.autoOnly === true;
  const audit = auditDataIntegrity();
  let repaired = 0;
  let heldBack = 0;
  // A repair that throws used to leave `_repairError` on the issue and a line in the console,
  // and nothing else. The return value carried the SUCCESS count only, so the caller announced
  // "Data reconciled: 3 issue(s) fixed" in green while a fourth repair had failed and the data
  // was still broken. A count of what worked is not a report. (audit 2026-08-03)
  const failures = [];

  audit.issues.forEach(issue => {
    if (issue.fix) {
      // ⚠ THE GATE. `autoOnly` is what the boot path passes; the Settings button passes nothing
      // and still repairs everything, because there a human asked for it.
      if (autoOnly && (issue.movesMoney || issue.rewritesPositions)) {
        heldBack++;
        console.warn('[Reconcile] HELD BACK (moves money, needs a click):', issue.msg);
        return;
      }
      try {
        issue.fix();
        repaired++;
        issue._repaired = true;
        console.log('[Reconcile] Fixed:', issue.msg);
      } catch (e) {
        console.error('[Reconcile] Repair failed:', issue.msg, e);
        issue._repairError = (e && e.message) || String(e);
        failures.push({ type: issue.type, severity: issue.severity, msg: issue.msg, error: issue._repairError });
      }
    }
  });

  if (repaired > 0) {
    // Sort cashTxs by date descending after adding reconciled entries
    S.cashTxs.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    save();
    console.log(`[Reconcile] ${repaired} issue(s) auto-repaired`);
  }

  if (failures.length) {
    console.warn('[Reconcile] ' + failures.length + ' repair(s) FAILED and the data is still broken:');
    failures.forEach(x => console.warn('  ' + x.severity + ' ' + x.type + ' — ' + x.msg + ' :: ' + x.error));
  }

  // Re-audit after repairs to get clean status
  const postAudit = auditDataIntegrity();
  return {
    preRepair: audit,
    postRepair: postAudit,
    repaired,
    heldBack,
    failed: failures.length,
    failures,
    // Whatever the repair pass did, this is the honest headline: is the book clean NOW?
    ok: postAudit.ok && failures.length === 0
  };
}
